// reCAPTCHA Enterprise (score-based, invisible), same key already set up for
// sniper-react and add2wallet.ai (GCP project sniper-add-to-wallet). Ported
// from add2walletio/src/lib/recaptcha.ts — identical pattern, CRA env var
// name instead of Vite's.
// NOTE: the key is domain-restricted in the reCAPTCHA console; add2wallet.link
// (and www.add2wallet.link) must be added to its allowed-domains list before
// tokens will actually validate from this site — until then getRecaptchaToken
// fails soft (returns undefined) and the server-side check fails open too
// (only enforced once RECAPTCHA_API_KEY is set on joinProgramResolver).

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, opts: { action: string }) => Promise<string>;
      };
    };
  }
}

const SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '6LfgNtkqAAAAALeo-d57n0smoL14mAJKhJN_fFyo';

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.grecaptcha?.enterprise) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Warm the script so the first submit doesn't pay the load cost. */
export function preloadRecaptcha(): void {
  loadScript().catch(() => {
    // non-fatal; token just won't be available and the server decides
  });
}

/**
 * Get a token for the given action, or undefined if reCAPTCHA is
 * unavailable (script blocked, domain not allowed yet, etc.). The server
 * only enforces tokens once RECAPTCHA_API_KEY is configured, so failing
 * soft here never strands a real user before activation.
 */
export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  try {
    await loadScript();
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha?.enterprise) return undefined;
    await new Promise<void>((resolve) => grecaptcha.enterprise.ready(resolve));
    return await grecaptcha.enterprise.execute(SITE_KEY, { action });
  } catch (err) {
    console.warn('reCAPTCHA token unavailable', err);
    return undefined;
  }
}
